// =============================================================================
// SimSense Marketplace — Setup Card Component
// =============================================================================
// Reusable card for displaying a marketplace setup listing.

import { formatLapTime } from "../../../utils/formatters";
import type { SetupProductPreview } from "../../../types/marketplace";
import "./SetupCard.css";

// Re-export the type for backward compat
export type { SetupProductPreview };

interface SetupCardProps {
  setup: SetupProductPreview;
  onInstall: (id: string) => void;
  onViewDetails: (id: string) => void;
  isSaved?: boolean;
  onToggleSave?: (id: string) => void;
}

function renderStars(rating: number) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return (
    <span className="stars" title={`${rating.toFixed(1)} out of 5`}>
      {"★".repeat(full)}
      {half ? "★" : ""}
      {"☆".repeat(empty)}
    </span>
  );
}

function formatPrice(cents: number, isFree: boolean) {
  if (isFree || cents === 0) return "FREE";
  return `$${(cents / 100).toFixed(2)}`;
}

function laneBadge(lane: string) {
  switch (lane) {
    case "shop":
      return <span className="lane-badge lane-shop">Shop</span>;
    case "pro_builder":
      return <span className="lane-badge lane-pro">Creator</span>;
    case "community":
      return <span className="lane-badge lane-community">Community</span>;
    default:
      return null;
  }
}

export function SetupCard({ setup, onInstall, onViewDetails, isSaved, onToggleSave }: SetupCardProps) {
  return (
    <div className={`setup-card lane-accent-${setup.lane}`} onClick={() => onViewDetails(setup.id)}>
      {/* Save/Wishlist button */}
      {onToggleSave && (
        <button
          className={`save-btn ${isSaved ? "saved" : ""}`}
          title={isSaved ? "Remove from saved" : "Save setup"}
          onClick={(e) => { e.stopPropagation(); onToggleSave(setup.id); }}
        >
          {isSaved ? "♥" : "♡"}
        </button>
      )}

      {/* Header: Setup Name, Car & Track */}
      <div className="setup-card-header">
        <h3 className="setup-name">{setup.setupName}</h3>
        <p className="car-name">{setup.carName}</p>
        <p className="track-name">{setup.trackName}</p>
        <div className="header-badges">
          {laneBadge(setup.lane)}
          <span className="season-badge">{setup.season}</span>
        </div>
      </div>

      {/* Creator Info */}
      <div className="setup-card-creator">
        {setup.creatorAvatarUrl ? (
          <img
            src={setup.creatorAvatarUrl}
            alt={setup.creatorName}
            className="creator-avatar"
          />
        ) : (
          <div className="creator-avatar-placeholder">
            {setup.creatorName.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="creator-details">
          <span className="creator-name">
            {setup.creatorName}
            {setup.isVerified && (
              <span className="verified-badge" title="Verified Creator">
                ✓
              </span>
            )}
          </span>
          <span className="setup-type-tag">{setup.type}</span>
        </div>
      </div>

      {/* Metrics Strip */}
      <div className="setup-card-metrics">
        <div className="rating">
          {renderStars(setup.rating)}
          <span className="review-count">({setup.reviewCount})</span>
        </div>
        <div className="downloads" title="Total Downloads">
          {setup.downloadCount.toLocaleString()} downloads
        </div>
      </div>

      {/* The Proof Block */}
      <div className="setup-card-proof">
        <div className="proof-label">PROVEN LAP TIME</div>
        <div className="proof-value">
          {formatLapTime(setup.bestLapTime)}
          {setup.telemetrySessionId && (
            <span className="proof-link">View Telemetry</span>
          )}
        </div>
      </div>

      {/* Footer: Price & Install */}
      <div
        className="setup-card-footer"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`price-tag ${setup.isFree ? "price-free" : ""}`}>
          {formatPrice(setup.priceCents, setup.isFree)}
        </div>
        <button
          className={`install-btn ${setup.isFree ? "btn-primary" : "btn-buy"}`}
          onClick={() => onInstall(setup.id)}
        >
          {setup.isFree ? "Install" : "Buy & Install"}
        </button>
      </div>
    </div>
  );
}
